import { useEffect, useRef, useMemo, useCallback, useSyncExternalStore } from 'react';
import { supabase } from '../lib/supabase';

// Re-export job status utilities
export { JOB_STATUS, isTerminalStatus, needsReview } from './jobStatus';

// Reconnection config
const INITIAL_RETRY_DELAY = 1000;
const MAX_RETRY_DELAY = 30000;
const MAX_RETRIES = 10;

// Connection states
export const CONNECTION_STATUS = {
  CONNECTED: 'connected',
  CONNECTING: 'connecting',
  DISCONNECTED: 'disconnected',
  ERROR: 'error',
};

// ============================================================================
// Singleton Connection Status Store
// ============================================================================
const connectionStore = {
  status: CONNECTION_STATUS.DISCONNECTED,
  listeners: new Set(),

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  },

  getSnapshot() {
    return this.status;
  },

  setStatus(status) {
    if (this.status !== status) {
      this.status = status;
      this.listeners.forEach(listener => listener());
    }
  }
};

/**
 * Hook to get realtime connection status.
 * Returns: 'connected' | 'connecting' | 'disconnected' | 'error'
 */
export function useRealtimeStatus() {
  return useSyncExternalStore(
    cb => connectionStore.subscribe(cb),
    () => connectionStore.getSnapshot()
  );
}

// ============================================================================
// Singleton Visibility Tracker
// ============================================================================
const visibilityStore = {
  isVisible: typeof document !== 'undefined' ? !document.hidden : true,
  listeners: new Set(),
  initialized: false,

  subscribe(listener) {
    this.listeners.add(listener);
    if (!this.initialized) {
      this._init();
    }
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0) {
        this._cleanup();
      }
    };
  },

  getSnapshot() {
    return this.isVisible;
  },

  _notify() {
    this.listeners.forEach(listener => listener());
  },

  _handleVisibilityChange() {
    const visible = !document.hidden;
    if (this.isVisible !== visible) {
      this.isVisible = visible;
      this._notify();
    }
  },

  _init() {
    if (this.initialized) return;
    this.initialized = true;
    this._boundHandler = this._handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this._boundHandler);
  },

  _cleanup() {
    if (!this.initialized) return;
    this.initialized = false;
    document.removeEventListener('visibilitychange', this._boundHandler);
  }
};

function usePageVisibility() {
  return useSyncExternalStore(
    cb => visibilityStore.subscribe(cb),
    () => visibilityStore.getSnapshot()
  );
}

/**
 * Subscribe to job status changes via Supabase Realtime.
 * 
 * Smart reconnection:
 * - Disconnects when tab is hidden (saves resources)
 * - Reconnects when tab becomes visible
 * - Exponential backoff on failures, resets when tab becomes visible
 * 
 * @param {Object} options
 * @param {string[]} options.portfolioIds - Portfolio IDs to watch
 * @param {function} options.onJobUpdate - Callback when any job is updated
 * @param {boolean} options.enabled - Whether subscription is active (default: true)
 */
export function useJobsRealtime({ portfolioIds = [], onJobUpdate, enabled = true }) {
  const channelRef = useRef(null);
  const callbackRef = useRef(onJobUpdate);
  const portfolioIdsRef = useRef(portfolioIds);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef(null);
  const isCleaningUpRef = useRef(false);
  const isSubscribingRef = useRef(false);

  const isPageVisible = usePageVisibility();
  const portfolioIdsKey = useMemo(() => portfolioIds.join(','), [portfolioIds]);

  useEffect(() => {
    callbackRef.current = onJobUpdate;
    portfolioIdsRef.current = portfolioIds;
  }, [onJobUpdate, portfolioIds]);

  const disconnect = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    isSubscribingRef.current = false;
    if (channelRef.current) {
      console.log('📡 Jobs realtime: disconnecting (tab hidden)');
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
      connectionStore.setStatus(CONNECTION_STATUS.DISCONNECTED);
    }
  }, []);

  const subscribe = useCallback(async () => {
    // Guard against concurrent subscribe calls (race condition during async getSession)
    if (isCleaningUpRef.current || channelRef.current || isSubscribingRef.current) return;
    
    isSubscribingRef.current = true;
    connectionStore.setStatus(CONNECTION_STATUS.CONNECTING);

    // Refresh auth token before connecting (prevents "Token has expired" errors)
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.warn('📡 Jobs realtime: failed to refresh session', error);
        // If refresh fails, the token is likely completely invalid - don't try to connect
        isSubscribingRef.current = false;
        connectionStore.setStatus(CONNECTION_STATUS.ERROR);
        return;
      }
      // IMPORTANT: Update the realtime client's access token
      // Without this, the realtime client continues using the old/expired token
      if (data?.session?.access_token) {
        await supabase.realtime.setAuth(data.session.access_token);
      }
    } catch (e) {
      console.warn('📡 Jobs realtime: session refresh error', e);
      isSubscribingRef.current = false;
      connectionStore.setStatus(CONNECTION_STATUS.ERROR);
      return;
    }

    if (isCleaningUpRef.current) {
      isSubscribingRef.current = false;
      return;
    }

    const channelName = `jobs-realtime-${portfolioIdsKey.slice(0, 50)}-${Date.now()}`;

    console.log('📡 Jobs realtime: connecting...');
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'jobs' },
        (payload) => {
          const job = payload.new;
          if (portfolioIdsRef.current.includes(job.portfolio_id)) {
            callbackRef.current?.(job);
          }
        }
      )
      .subscribe((status, err) => {
        if (isCleaningUpRef.current) return;
        
        if (status === 'SUBSCRIBED') {
          console.log('📡 Jobs realtime: connected');
          retryCountRef.current = 0;
          connectionStore.setStatus(CONNECTION_STATUS.CONNECTED);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn(`📡 Jobs realtime ${status}:`, err);
          connectionStore.setStatus(CONNECTION_STATUS.ERROR);
          
          isSubscribingRef.current = false;
          if (channelRef.current) {
            supabase.removeChannel(channelRef.current);
            channelRef.current = null;
          }
          
          if (retryCountRef.current < MAX_RETRIES && !isCleaningUpRef.current) {
            const delay = Math.min(
              INITIAL_RETRY_DELAY * Math.pow(2, retryCountRef.current),
              MAX_RETRY_DELAY
            );
            retryCountRef.current++;
            
            console.log(`📡 Jobs realtime: reconnecting in ${delay}ms (attempt ${retryCountRef.current}/${MAX_RETRIES})`);
            
            retryTimeoutRef.current = setTimeout(() => {
              if (!isCleaningUpRef.current) {
                subscribe();
              }
            }, delay);
          } else if (retryCountRef.current >= MAX_RETRIES) {
            console.error('📡 Jobs realtime: max retries reached');
          }
        }
      });

    channelRef.current = channel;
    isSubscribingRef.current = false;
  }, [portfolioIdsKey]);

  // Handle visibility changes
  useEffect(() => {
    if (!enabled || portfolioIds.length === 0) return;

    if (isPageVisible) {
      if (!channelRef.current) {
        console.log('📡 Jobs realtime: tab visible, reconnecting');
        retryCountRef.current = 0;
        subscribe();
      }
    } else {
      disconnect();
    }
  }, [isPageVisible, enabled, portfolioIds.length, subscribe, disconnect]);

  // Initial setup and cleanup
  useEffect(() => {
    if (!enabled || portfolioIds.length === 0) return;

    isCleaningUpRef.current = false;
    retryCountRef.current = 0;
    
    if (isPageVisible) {
      subscribe();
    }

    return () => {
      console.log('📡 Jobs realtime: cleaning up');
      isCleaningUpRef.current = true;
      disconnect();
    };
  }, [enabled, portfolioIds.length, portfolioIdsKey, subscribe, disconnect, isPageVisible]);

  return null;
}
